function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import "core-js/modules/es.object.to-string.js";
import "core-js/modules/web.dom-collections.for-each.js";
import "core-js/modules/es.function.name.js";
import "core-js/modules/es.object.keys.js";
import "core-js/modules/es.symbol.js";
import "core-js/modules/es.array.filter.js";
import "core-js/modules/es.object.get-own-property-descriptor.js";
import "core-js/modules/es.object.get-own-property-descriptors.js";
import { callFunctionOnTelemetrySinks } from './callFunctionOnTelemetrySinks';
import { getPLTMetrics } from './sinks/utils/getPLTMetrics';
import { stringifyHelper } from './sinks/utils/stringifyHelper';
import { getErrorJsonData } from './sinks/utils/getErrorJsonData';
import { getInstanceId } from './getInstanceId';
import { getLCP, getFID, getCLS } from 'web-vitals';
import { getJSResourcesMetrics } from './sinks/utils/getResourcesMetrics';
import { addPageUnloadEventListener, removePageUnloadEventListener } from '@microsoft/applicationinsights-core-js';

/**
 * Create a telemetry instance and initialize it
 * @param params The params that includes array of providers and context
 */
export function initializeTelemetry(params) {
  var telemetrySinks = [];
  var instanceId = getInstanceId();
  var context = [params.data, {
    sessionId: instanceId
  }, function () {
    return {
      pathname: window.location.pathname
    };
  }];
  params.sinkFactories.forEach(function (sinkFactory) {
    return telemetrySinks.push(sinkFactory({
      getContextData: getContextData
    }));
  });

  function getContextData() {
    return (context !== null && context !== void 0 ? context : []).reduce(function (acc, value) {
      if (!(value instanceof Function)) {
        return _objectSpread(_objectSpread({}, acc), value);
      }

      try {
        return _objectSpread(_objectSpread({}, acc), value());
      } catch (error) {}

      return acc;
    }, {
      sessionId: instanceId,
      namespace: '',
      userId: '',
      tenantId: '',
      pathname: '',
      env: ''
    });
  }

  function reportWebVitals(metric) {
    var name = metric.name,
        value = metric.value;
    telemetry.reportPerformance(name, {
      webVital: value
    });
  } // Send core web-vitals
  // Reference: https://web.dev/vitals/


  getCLS(reportWebVitals);
  getFID(reportWebVitals);
  getLCP(reportWebVitals); // Track resource error that will be handled by error event listener in capture phase

  var errorHandler = function errorHandler(errorEvent) {
    var target = errorEvent.target;
    var isElementTarget = target instanceof HTMLScriptElement || target instanceof HTMLLinkElement || target instanceof HTMLImageElement; // Report only for resource type as windows.onerror can report non-resource error. This can prevent duplicated report.

    if (isElementTarget) {
      telemetry.error('resourceError', {
        error: getErrorJsonData(errorEvent.error),
        message: errorEvent.message,
        src: target instanceof HTMLLinkElement ? target.href : target.src
      });
    } else {
      var _errorEvent$error;

      telemetry.error('jsError', {
        message: errorEvent.message,
        stack: (_errorEvent$error = errorEvent.error) === null || _errorEvent$error === void 0 ? void 0 : _errorEvent$error.stack
      });
    }
  };

  var onLoadHandler = function onLoadHandler() {
    // Log the browser-perceived performance metrics
    telemetry.reportPerformance('PLT', getPLTMetrics()); // Log the JS resources metrics

    var _getJSResourcesMetric = getJSResourcesMetrics(telemetry),
        internalResource = _getJSResourcesMetric.internalResource,
        combinedExternalResource = _getJSResourcesMetric.combinedExternalResource;

    if (combinedExternalResource.totalResourceCount) {
      telemetry.reportPerformance('external-js-resources', combinedExternalResource);
    }

    if (internalResource.resources.length) {
      telemetry.reportPerformance('internal-js-resources', internalResource);
    }
  };

  var unloadHandler = function unloadHandler(ev) {
    // log the duration from navigation start until the page unloads
    // event type can be beforeunload, pagehide, and unload
    var eventType = ev.type;
    telemetry.reportPerformance(eventType !== null && eventType !== void 0 ? eventType : 'page-unload', {
      duration: performance.now(),
      eventType: eventType
    });
    telemetry.destroy();
  };

  var unhandledrejectionHandler = function unhandledrejectionHandler(ev) {
    unhandledrejectionTask(ev, telemetry);
  }; // Register global event handlers


  addPageUnloadEventListener(unloadHandler);
  window.addEventListener('load', onLoadHandler);
  window.addEventListener('error', errorHandler, true);
  window.addEventListener('unhandledrejection', unhandledrejectionHandler); // TODO: move the creation in a create function and add an extend method

  var telemetry = {
    reportCustomEvent: function reportCustomEvent(name, data) {
      callFunctionOnTelemetrySinks('reportCustomEvent', [{
        name: name,
        data: data,
        context: getContextData()
      }], telemetrySinks);
    },
    reportPerformance: function reportPerformance(name, data) {
      callFunctionOnTelemetrySinks('reportPerformance', [{
        name: name,
        data: data,
        context: getContextData()
      }], telemetrySinks);
    },
    reportQos: function reportQos(name, data) {
      callFunctionOnTelemetrySinks('reportQos', [{
        name: name,
        data: data,
        context: getContextData()
      }], telemetrySinks);
    },
    warn: function warn(name, data) {
      callFunctionOnTelemetrySinks('warn', [{
        name: name,
        data: data,
        context: getContextData()
      }], telemetrySinks);
    },
    error: function error(name, data) {
      callFunctionOnTelemetrySinks('error', [{
        name: name,
        data: data,
        context: getContextData()
      }], telemetrySinks);
    },
    destroy: function destroy() {
      callFunctionOnTelemetrySinks('destroy', [getContextData()], telemetrySinks);
      telemetrySinks = []; // Unregister global event handlers

      removePageUnloadEventListener(unloadHandler);
      window.removeEventListener('load', onLoadHandler);
      window.removeEventListener('error', errorHandler, true);
      window.removeEventListener('unhandledrejection', unhandledrejectionHandler);
    }
  };
  return telemetry;
}
export var unhandledrejectionTask = function unhandledrejectionTask(ev, telemetry) {
  var _ev$reason;

  // Stringify the reason object to prevent event drop if it contains array field
  if (((_ev$reason = ev.reason) === null || _ev$reason === void 0 ? void 0 : _ev$reason.name) === 'AxiosError') {
    telemetry.error(ev.type, stringifyHelper(ev.reason.message));
  } else {
    telemetry.error(ev.type, stringifyHelper(ev.reason));
  }
};
//# sourceMappingURL=telemetry.js.map